import type { CreateBoardResponse } from "../shared/types";
import { copyText, element } from "./dom";
import { logOut } from "./site-gate";

function resultRow(labelText: string, value: string): HTMLDivElement {
  const label = element("span", { className: "result-row__label", text: labelText });
  const output = element("code", { className: "result-row__url", text: value });
  const copy = element("button", { className: "button button--secondary button--small", text: "Kopieer" });
  copy.addEventListener("click", async () => {
    copy.textContent = (await copyText(value)) ? "Gekopieerd" : "Niet gelukt";
    window.setTimeout(() => (copy.textContent = "Kopieer"), 1_500);
  });
  return element("div", { className: "result-row", children: [label, output, copy] });
}

export function renderCreatePage(app: HTMLElement): void {
  document.body.className = "app-body";
  document.title = "IRL Bingo · Nieuwe kaart";

  const brandMark = element("div", { className: "brand__mark", text: "B", attrs: { "aria-hidden": "true" } });
  const brandText = element("div", {
    children: [
      element("strong", { className: "brand__name", text: "IRL BINGO" }),
      element("span", { className: "brand__sub", text: "LIVE CONTROL" }),
    ],
  });
  const brand = element("div", { className: "brand", children: [brandMark, brandText] });
  const logout = element("button", { className: "button button--ghost button--small", text: "Uitloggen" });
  logout.addEventListener("click", () => void logOut());
  const header = element("header", { className: "site-header", children: [brand, logout] });

  const eyebrow = element("p", { className: "eyebrow", text: "KLAAR VOOR DE VOLGENDE STREAM" });
  const title = element("h1", { className: "hero__title", text: "Negen momenten. Eén bingokaart." });
  const intro = element("p", {
    className: "hero__intro",
    text: "Maak een kaart aan, voeg de overlay toe aan OBS en bedien ieder vak live vanaf je telefoon of laptop.",
  });
  const createButton = element("button", { className: "button button--primary button--large", text: "Nieuwe kaart maken" });
  const feedback = element("p", { className: "form-error", attrs: { role: "alert" } });
  const result = element("section", { className: "create-result", attrs: { "aria-live": "polite" } });
  const heroCopy = element("div", {
    className: "hero__copy",
    children: [eyebrow, title, intro, createButton, feedback],
  });

  const miniCells = Array.from({ length: 9 }, (_, index) =>
    element("div", {
      className: `hero-board__cell${index === 4 || index === 7 ? " is-demo-complete" : ""}`,
      children: [
        element("span", {
          text: ["Spontane fan", "Verkeerde afslag", "Lokale snack", "Stream sniped", "Bus gemist", "Nieuw uitzicht", "Lekker weer", "Rare vraag", "Plot twist"][index],
        }),
      ],
    }),
  );
  const demo = element("div", { className: "hero-board", children: miniCells });
  const hero = element("section", { className: "hero", children: [heroCopy, demo] });
  const main = element("main", { className: "site-main", children: [hero, result] });
  const shell = element("div", { className: "site-shell", children: [header, main] });
  app.replaceChildren(shell);

  createButton.addEventListener("click", async () => {
    createButton.disabled = true;
    createButton.textContent = "Kaart wordt gemaakt…";
    feedback.textContent = "";
    result.replaceChildren();
    try {
      const response = await fetch("/api/boards", {
        method: "POST",
        headers: { accept: "application/json" },
      });
      const body = (await response.json()) as CreateBoardResponse & { error?: string };
      if (!response.ok) throw new Error(body.error ?? "Aanmaken is niet gelukt.");

      const overlayUrl = `${location.origin}/overlay/${body.boardId}`;
      const controlUrl = `${location.origin}/control/${body.boardId}#token=${body.adminToken}`;
      const resultTitle = element("h2", { className: "create-result__title", text: "Je kaart staat klaar" });
      const warning = element("p", {
        className: "security-note",
        text: "Bewaar de beheerlink privé. Iedereen met deze link en het sitewachtwoord kan de kaart aanpassen.",
      });
      const openControl = element("a", {
        className: "button button--primary",
        text: "Open bedieningspaneel",
        attrs: { href: controlUrl },
      });
      result.append(resultTitle, resultRow("OBS-overlay", overlayUrl), resultRow("Privé beheerlink", controlUrl), warning, openControl);
      result.scrollIntoView({ behavior: "smooth", block: "nearest" });
    } catch (cause) {
      feedback.textContent = cause instanceof Error ? cause.message : "Aanmaken is niet gelukt.";
    } finally {
      createButton.disabled = false;
      createButton.textContent = "Nieuwe kaart maken";
    }
  });
}
